import { FONT_STYLES } from '@/shared/styles/constants';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import styled from '@emotion/native';
import { Calendar as RNCalendar, DateData } from 'react-native-calendars';
import { Direction } from 'react-native-calendars/src/types';
import XDate from 'xdate';
import { format, parseISO, isValid, addMonths, startOfDay } from 'date-fns';
import { Feather } from '@expo/vector-icons';
import useExploreStore from '@/stores/useExploreStore';
import {
  green,
  white,
  mintGreenAlt,
  mint,
  lightGreen,
  alphaBlack50,
  disabledLightGrey,
  disabledDarkestGrey,
} from '@/shared/styles/colors';
import { Label, PaddingContainer } from '../styledComponents';
import { AvailableTime } from '../BookCalendar';
import {
  getWindowBounds,
  buildDisabledMap,
  applyAvailability,
  markSelectedDay,
  toCalendarDateString,
  isDateInAvailableTimes,
  getFirstSelectableDate,
} from './utils';
import {
  CALENDAR_MONTH_FORMAT,
  CALENDAR_MONTH_FORMAT_DISPLAY,
} from '../shared/constants';
import { LoraText } from '@/shared/components/StyledText';

interface CalendarProps {
  availableTimes: AvailableTime[];
  loading: boolean;
}

const Calendar = ({ availableTimes, loading }: CalendarProps) => {
  const { booking, setBooking } = useExploreStore();

  const { minDate, maxDate, currentMonth } = useMemo(
    () => getWindowBounds(availableTimes, loading),
    [availableTimes, loading],
  );

  const minDateString = toCalendarDateString(minDate);
  const maxDateString = toCalendarDateString(maxDate);

  const markedDates = useMemo(() => {
    // start with all days disabled
    const today = startOfDay(new Date());
    const map = buildDisabledMap(today, maxDate);

    applyAvailability(map, availableTimes);
    markSelectedDay(map, booking?.selectedDate ?? undefined);

    return map;
  }, [availableTimes, booking?.selectedDate, maxDate]);

  // Auto-select first available date if needed
  useEffect(() => {
    if (loading && booking?.selectedDate) {
      setBooking({ selectedDate: undefined, selectedTime: undefined });
      return;
    }
    if (loading || availableTimes.length === 0) return;

    const isValid = booking?.selectedDate
      ? isDateInAvailableTimes(booking.selectedDate, availableTimes)
      : false;
    if (!isValid) {
      const selectedDate = getFirstSelectableDate(availableTimes);
      if (selectedDate) {
        setBooking({ selectedDate, selectedTime: undefined });
      }
    }
  }, [availableTimes, loading, booking?.selectedDate, setBooking]);

  // State for tracking the currently displayed month in the calendar
  const [displayedMonth, setDisplayedMonth] = useState(currentMonth);

  const monthHasAvailability = useCallback(
    (month: string) =>
      Object.keys(markedDates).some(
        (dateKey) =>
          dateKey.startsWith(month) && !markedDates[dateKey].disabled,
      ),
    [markedDates],
  );

  // Check if adjacent months have availability
  const { nextMonthHasAvailability, prevMonthHasAvailability } = useMemo(() => {
    const currentMonthDate = parseISO(`${displayedMonth}-01`);
    if (!isValid(currentMonthDate)) {
      return {
        nextMonthHasAvailability: false,
        prevMonthHasAvailability: false,
      };
    }

    const nextMonthDate = addMonths(currentMonthDate, 1);
    const prevMonthDate = addMonths(currentMonthDate, -1);
    const todayMonthString = format(new Date(), CALENDAR_MONTH_FORMAT);

    return {
      nextMonthHasAvailability: monthHasAvailability(
        format(nextMonthDate, CALENDAR_MONTH_FORMAT),
      ),
      prevMonthHasAvailability:
        format(prevMonthDate, CALENDAR_MONTH_FORMAT) >= todayMonthString ||
        monthHasAvailability(format(prevMonthDate, CALENDAR_MONTH_FORMAT)),
    };
  }, [displayedMonth, monthHasAvailability]);

  const onDayPress = useCallback(
    (day: DateData) => {
      const markedDate = markedDates[day.dateString];
      if (!markedDate || markedDate.disabled) return;

      const date = startOfDay(parseISO(day.dateString));
      setBooking({ selectedDate: date, selectedTime: undefined });
    },
    [markedDates, setBooking],
  );

  const onMonthChange = useCallback((month: DateData) => {
    const newMonth = month.dateString.substring(0, 7);
    setDisplayedMonth(newMonth);
  }, []);

  const isLeftArrowDisabled = !prevMonthHasAvailability;
  const isRightArrowDisabled = !nextMonthHasAvailability;

  function renderArrow(direction: Direction) {
    const isDisabled =
      (direction === 'left' && isLeftArrowDisabled) ||
      (direction === 'right' && isRightArrowDisabled);

    return (
      <ArrowCircle isDisabled={isDisabled}>
        <Feather
          name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
          size={18}
          color={isDisabled ? disabledDarkestGrey : lightGreen}
        />
      </ArrowCircle>
    );
  }

  function renderHeader(date?: XDate) {
    if (!date) {
      return null;
    }
    const month = date.toString(CALENDAR_MONTH_FORMAT_DISPLAY).toUpperCase();
    return <MonthTitle>{month}</MonthTitle>;
  }

  return (
    <>
      <PaddingContainer marginBottom={20}>
        <Label>Select the date:</Label>
      </PaddingContainer>
      <CalendarContainer>
        <RNCalendar
          minDate={minDateString}
          maxDate={maxDateString}
          onDayPress={onDayPress}
          onMonthChange={onMonthChange}
          markedDates={markedDates}
          disableAllTouchEventsForDisabledDays={true}
          disableArrowRight={isRightArrowDisabled}
          disableArrowLeft={isLeftArrowDisabled}
          displayLoadingIndicator={loading}
          renderArrow={renderArrow}
          renderHeader={renderHeader}
          theme={{
            calendarBackground: 'transparent',
            selectedDayBackgroundColor: green,
            selectedDayTextColor: white,
            dayTextColor: green,
            textDisabledColor: mintGreenAlt,
            textDayFontFamily: FONT_STYLES.helvetica.fontFamily,
            textDayFontSize: 16,
            textDayFontWeight: 700,
            textSectionTitleColor: alphaBlack50,
            textDayHeaderFontSize: 10.5,
            textDayHeaderFontFamily: FONT_STYLES.helvetica.fontFamily,
            ...({
              'stylesheet.calendar.main': {
                container: {
                  paddingLeft: 5,
                  paddingRight: 5,
                  backgroundColor: 'transparent',
                },
              },
            } as Parameters<typeof RNCalendar>[0]['theme']),
          }}
        />
      </CalendarContainer>
    </>
  );
};

const CalendarContainer = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  padding: 16,
  marginBottom: 24,
  width: '100%',
});

const ArrowCircle = styled.View<{ isDisabled: boolean }>(({ isDisabled }) => ({
  backgroundColor: isDisabled ? disabledLightGrey : mint,
  borderRadius: '50%',
  width: 26,
  height: 26,
  alignItems: 'center',
  justifyContent: 'center',
}));

const MonthTitle = styled(LoraText)({
  fontSize: 18,
  fontWeight: 500,
  color: green,
  letterSpacing: 0.18,
});

export default Calendar;
